import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaTimes, FaCopy, FaCheck, FaSpinner, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { FaPix } from 'react-icons/fa6';
import io from 'socket.io-client';
import {
  createPixPayment,
  createPixPaymentPublic,
  getPaymentStatus,
  getPaymentStatusPublic,
  getCurrentAccessToken
} from '../../services/api';
import getApiUrl from '../../utils/getApiUrl';
import { formatPriceWithSymbol } from '../../utils/currency';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 420px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 20px;
  color: #666;
  cursor: pointer;
  z-index: 1;

  &:hover {
    color: #333;
  }
`;

const ModalContent = styled.div`
  padding: 24px;
  text-align: center;
`;

const PixIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #00d4aa, #00a884);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;

  svg {
    font-size: 28px;
    color: white;
  }
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 14px;
  margin: 0 0 24px;
`;

const PaymentAmount = styled.div`
  background: #f8f9fa;
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 24px;

  span {
    display: block;
    font-size: 14px;
    color: #666;
    margin-bottom: 4px;
  }

  strong {
    font-size: 28px;
    color: #00a884;
    font-weight: 700;
  }
`;

const QRCodeContainer = styled.div`
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
`;

const QRCodeImage = styled.img`
  max-width: 180px;
  max-height: 180px;
`;

const QRCodePlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #ccc;

  svg {
    font-size: 80px;
    margin-bottom: 16px;
  }
`;

const PixCodeBox = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const PixCodeInput = styled.input`
  flex: 1;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 12px;
  font-family: monospace;
  background: #f8f9fa;
  color: #333;

  &:focus {
    outline: none;
    border-color: #00a884;
  }
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  background: ${({ $copied }) => ($copied ? '#4caf50' : 'linear-gradient(135deg, #00d4aa, #00a884)')};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ExpirationInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: ${({ $warning }) => ($warning ? '#fff3e0' : '#e3f2fd')};
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  color: ${({ $warning }) => ($warning ? '#e65100' : '#1565c0')};

  strong {
    font-weight: 600;
  }
`;

const Instructions = styled.div`
  text-align: left;
  background: #f8f9fa;
  padding: 16px;
  border-radius: 12px;

  h3 {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin: 0 0 12px;
  }

  ol {
    margin: 0;
    padding-left: 20px;

    li {
      font-size: 13px;
      color: #666;
      margin-bottom: 8px;
      line-height: 1.4;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
`;

const StatusOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;

  svg {
    font-size: 64px;
    margin-bottom: 16px;
  }

  h3 {
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 8px;
  }

  p {
    color: #666;
    margin: 0;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;

  svg {
    font-size: 40px;
    color: #00a884;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ModalPaymentPix = ({
  isOpen,
  onClose,
  amount,
  pedidoId,
  customer,
  idEmpresa,
  onPaymentSuccess,
  onPaymentFailed,
  onTransactionCreated // ✅ Callback para informar ao pai sobre a transação criada
}) => {
  const { t } = useTranslation('cardapio');

  const [loading, setLoading] = useState(true);
  const [qrCode, setQrCode] = useState(null);
  const [qrCodeBase64, setQrCodeBase64] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [expirationDate, setExpirationDate] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState('');
  const [copied, setCopied] = useState(false);

  const pollingIntervalRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const lastTimeUpdateRef = useRef('');
  const socketRef = useRef(null);
  const hasInitializedRef = useRef(false); // ✅ Controle para evitar múltiplas inicializações

  // ✅ Refs para callbacks (evitar stale closures no WebSocket)
  const onPaymentSuccessRef = useRef(onPaymentSuccess);
  const onPaymentFailedRef = useRef(onPaymentFailed);
  const onCloseRef = useRef(onClose);

  // Atualizar refs quando callbacks mudarem
  useEffect(() => {
    onPaymentSuccessRef.current = onPaymentSuccess;
    onPaymentFailedRef.current = onPaymentFailed;
    onCloseRef.current = onClose;
  }, [onPaymentSuccess, onPaymentFailed, onClose]);

  // ✅ Chave para armazenar transação no localStorage (por pedido)
  const getStorageKey = () => `pix_transaction_${pedidoId}`;

  // ✅ Recuperar transação existente do localStorage
  const getStoredTransaction = () => {
    try {
      const stored = localStorage.getItem(getStorageKey());
      if (stored) {
        const data = JSON.parse(stored);
        // Verificar se não expirou
        const expDate = new Date(data.expirationDate);
        if (expDate > new Date()) {
          console.log('[PIX-MODAL] ♻️ Transação existente encontrada no localStorage:', data.transactionId);
          return data;
        } else {
          console.log('[PIX-MODAL] ⏰ Transação expirada, removendo do localStorage');
          localStorage.removeItem(getStorageKey());
        }
      }
    } catch (error) {
      console.error('[PIX-MODAL] Erro ao recuperar transação do localStorage:', error);
    }
    return null;
  };

  // ✅ Salvar transação no localStorage
  const saveTransaction = (data) => {
    try {
      localStorage.setItem(getStorageKey(), JSON.stringify({
        transactionId: data.transaction_id,
        qrCode: data.qr_code,
        qrCodeBase64: data.qr_code_base64,
        qrCodeUrl: data.qr_code_url,
        expirationDate: data.expiration_date,
        status: data.status,
        amount: amount,
        createdAt: new Date().toISOString()
      }));
      console.log('[PIX-MODAL] 💾 Transação salva no localStorage:', data.transaction_id);
    } catch (error) {
      console.error('[PIX-MODAL] Erro ao salvar transação:', error);
    }
  };

  // ✅ Limpar transação do localStorage (quando pagamento for concluído)
  const clearStoredTransaction = () => {
    localStorage.removeItem(getStorageKey());
    console.log('[PIX-MODAL] 🗑️ Transação removida do localStorage');
  };

  // ✅ Inicializar modal - usar transação existente ou criar nova
  useEffect(() => {
    if (!isOpen) {
      hasInitializedRef.current = false; // Reset ao fechar
      return;
    }

    // Evitar múltiplas inicializações
    if (hasInitializedRef.current) {
      console.log('[PIX-MODAL] ⚠️ Já inicializado, ignorando...');
      return;
    }

    // ✅ PIX não exige CPF do cliente - removida validação de customer.document

    hasInitializedRef.current = true;

    // ✅ Verificar se já existe transação para este pedido
    const existingTransaction = getStoredTransaction();

    if (existingTransaction) {
      console.log('[PIX-MODAL] ♻️ Reutilizando transação existente:', existingTransaction.transactionId);

      // Restaurar dados da transação existente
      setQrCode(existingTransaction.qrCode);
      setQrCodeBase64(existingTransaction.qrCodeBase64);
      setQrCodeUrl(existingTransaction.qrCodeUrl);
      setTransactionId(existingTransaction.transactionId);
      setExpirationDate(new Date(existingTransaction.expirationDate));
      setPaymentStatus(existingTransaction.status || 'pending');
      setLoading(false);

      // Iniciar polling para verificar status
      startPolling(existingTransaction.transactionId);
    } else {
      // ✅ Criar nova transação
      console.log('[PIX-MODAL] 🆕 Criando nova transação PIX...');
      generatePixPayment();
    }

    return () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [isOpen, pedidoId]);

  // Timer de expiração
  useEffect(() => {
    if (!expirationDate || paymentStatus !== 'pending') return;

    const updateTimer = () => {
      const now = new Date();
      const diff = expirationDate - now;

      if (diff <= 0) {
        setTimeRemaining('Expirado');
        setPaymentStatus('expired');
        if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);

        // ✅ Limpar transação expirada do localStorage
        clearStoredTransaction();
        console.log('[PIX-MODAL] ⏰ QR Code expirado - transação removida do localStorage');

        // ✅ Desconectar WebSocket quando expira
        if (socketRef.current) {
          socketRef.current.disconnect();
          socketRef.current = null;
          console.log('[PIX-MODAL] 🔌 WebSocket desconectado após expiração');
        }
        return;
      }

      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      const newTime = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

      if (newTime !== lastTimeUpdateRef.current) {
        lastTimeUpdateRef.current = newTime;
        setTimeRemaining(newTime);
      }
    };

    updateTimer();
    timerIntervalRef.current = setInterval(updateTimer, 1000);

    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [expirationDate, paymentStatus]);

  // ✅ WebSocket para atualizações em tempo real
  // IMPORTANTE: Mantém conexão ativa mesmo quando modal é fechado
  // Só desconecta quando pagamento for concluído ou componente desmontado
  useEffect(() => {
    // Não conectar se não tiver transactionId ou se pagamento já foi concluído
    if (!transactionId || paymentStatus === 'paid' || paymentStatus === 'failed' || paymentStatus === 'cancelled') {
      return;
    }

    // Se já tiver socket conectado, não criar novo
    if (socketRef.current?.connected) {
      console.log('[PIX-MODAL] ⚠️ WebSocket já conectado, ignorando...');
      return;
    }

    const API_URL = getApiUrl();
    const userEmpresa = JSON.parse(localStorage.getItem('userEmpresa') || localStorage.getItem('empresa') || '{}');
    const empresaId = idEmpresa || userEmpresa.id_empresa;

    if (!empresaId) {
      console.warn('[PIX-MODAL] Empresa não encontrada - WebSocket não conectado');
      return;
    }

    console.log(`[PIX-MODAL] 🔌 Conectando WebSocket para transaction ${transactionId} em ${API_URL}`);

    const socket = io(API_URL, {
      withCredentials: true,
      transports: ['websocket'],
      auth: { token: getCurrentAccessToken() },
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log(`[PIX-MODAL] ✅ WebSocket conectado - Entrando na sala empresa_${empresaId}`);
      socket.emit('joinCompanyRoom', {
        companyId: empresaId.toString(),
        clientId: `pix_modal_${Date.now()}`
      });
    });

    socket.on('pix_payment_confirmed', (data) => {
      console.log('[PIX-MODAL] 🎉 Pagamento confirmado via WebSocket:', data);
      if (data.transaction_id === transactionId) {
        setPaymentStatus('paid');
        clearStoredTransaction(); // ✅ Limpar transação do localStorage
        toast.success(t('modal.pix.pixConfirmedToast'));
        if (onPaymentSuccessRef.current) {
          onPaymentSuccessRef.current({
            transactionId: data.transaction_id,
            pedido_id: data.pedido_id,
            amount: data.amount,
            method: data.payment_method,
            paidAt: new Date()
          });
        }
        // Desconectar socket após pagamento confirmado
        socket.disconnect();
        socketRef.current = null;
      }
    });

    socket.on('pix_payment_failed', (data) => {
      console.log('[PIX-MODAL] ❌ Pagamento falhou via WebSocket:', data);
      if (data.transaction_id === transactionId) {
        setPaymentStatus('failed');
        clearStoredTransaction(); // ✅ Limpar transação do localStorage
        toast.error(t('modal.pix.pixFailedToast'));
        if (onPaymentFailedRef.current) {
          onPaymentFailedRef.current({ status: 'failed', error: data.error });
        }
        // Desconectar socket após falha
        socket.disconnect();
        socketRef.current = null;
      }
    });

    socket.on('pix_payment_cancelled', (data) => {
      console.log('[PIX-MODAL] 🚫 Pagamento cancelado via WebSocket:', data);
      if (data.transaction_id === transactionId) {
        setPaymentStatus('cancelled');
        clearStoredTransaction(); // ✅ Limpar transação do localStorage
        toast.warning(t('modal.pix.pixCancelledToast'));
        // Desconectar socket após cancelamento
        socket.disconnect();
        socketRef.current = null;
      }
    });

    // Cleanup: Desconectar APENAS quando componente for desmontado
    // NÃO desconectar quando modal simplesmente fechar (isOpen = false)
    return () => {
      console.log('[PIX-MODAL] 🔌 Limpando WebSocket (componente desmontado ou pagamento concluído)');
      if (socketRef.current) {
        socket.off('pix_payment_confirmed');
        socket.off('pix_payment_failed');
        socket.off('pix_payment_cancelled');
        socket.disconnect();
        socketRef.current = null;
      }
    };
  }, [transactionId, paymentStatus, idEmpresa]); // ✅ Removido isOpen, onPaymentSuccess, onPaymentFailed, onClose das dependências

  const generatePixPayment = async () => {
    try {
      setLoading(true);
      setPaymentStatus('pending');
      setQrCode(null);
      setQrCodeBase64(null);
      setQrCodeUrl(null);

      const isPublic = !!idEmpresa;

      const paymentData = {
        amount,
        customer,
        expiration_minutes: 30,
        pedido_id: pedidoId
      };

      console.log('[PIX-MODAL] Enviando pagamento:', paymentData);

      const response = isPublic
        ? await createPixPaymentPublic({ ...paymentData, id_empresa: idEmpresa })
        : await createPixPayment(paymentData);

      if (response.success) {
        setQrCode(response.qr_code);
        setQrCodeBase64(response.qr_code_base64);
        setQrCodeUrl(response.qr_code_url);
        setTransactionId(response.transaction_id);
        setExpirationDate(new Date(response.expiration_date));
        setPaymentStatus(response.status);

        console.log('[PIX-MODAL] QR Code recebido:', {
          qr_code_url: response.qr_code_url,
          qr_code_base64_length: response.qr_code_base64?.length
        });

        // ✅ Salvar transação no localStorage para reutilização
        saveTransaction(response);

        // ✅ Notificar componente pai sobre a transação criada
        if (onTransactionCreated) {
          onTransactionCreated(response.transaction_id);
        }

        startPolling(response.transaction_id);
      } else {
        throw new Error(response.error || t('common:pixGenerateError'));
      }
    } catch (error) {
      console.error('[PIX-MODAL] Erro ao gerar PIX:', error);
      toast.error(error.response?.data?.error || error.message || t('common:pixCodeError'));

      if (onPaymentFailed) {
        onPaymentFailed(error);
      }
      setTimeout(() => onClose(), 2000);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Função para regenerar QR Code quando expirar
  const handleRegeneratePixCode = () => {
    console.log('[PIX-MODAL] 🔄 Regenerando QR Code...');

    // Resetar estados
    setQrCode(null);
    setQrCodeBase64(null);
    setQrCodeUrl(null);
    setTransactionId(null);
    setExpirationDate(null);
    setTimeRemaining('');
    setPaymentStatus('pending');
    setCopied(false);

    // Resetar ref de inicialização para permitir nova geração
    hasInitializedRef.current = false;

    // Gerar novo QR Code
    generatePixPayment();
  };

  const startPolling = useCallback((txId) => {
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const isPublic = !!idEmpresa;
        const response = isPublic
          ? await getPaymentStatusPublic(txId)
          : await getPaymentStatus(txId);

        if (response.success && response.status !== 'pending') {
          setPaymentStatus(response.status);

          if (response.status === 'paid') {
            toast.success(t('modal.pix.pixConfirmedToast'));
            clearInterval(pollingIntervalRef.current);
            clearStoredTransaction(); // ✅ Limpar transação do localStorage
            if (onPaymentSuccess) {
              onPaymentSuccess({ transactionId: txId, amount, paidAt: new Date() });
            }
            setTimeout(() => onClose(), 2000);
          } else if (response.status === 'failed' || response.status === 'cancelled') {
            clearInterval(pollingIntervalRef.current);
            clearStoredTransaction(); // ✅ Limpar transação do localStorage
            if (onPaymentFailed) {
              onPaymentFailed({ status: response.status });
            }
            setTimeout(() => onClose(), 2000);
          }
        }
      } catch (error) {
        console.error('[PIX-MODAL] Erro no polling:', error);
      }
    }, 5000);
  }, [idEmpresa, amount, onPaymentSuccess, onPaymentFailed, onClose]);

  const copyPixCode = () => {
    if (!qrCode) return;
    navigator.clipboard.writeText(qrCode).then(() => {
      setCopied(true);
      toast.success(t('modal.pix.codeCopied'));
      setTimeout(() => setCopied(false), 3000);
    }).catch(() => {
      toast.error(t('modal.pix.copyError'));
    });
  };

  const handleClose = () => {
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={handleClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose}>
          <FaTimes />
        </CloseButton>

        <ModalContent>
          {loading ? (
            <LoadingSpinner>
              <FaSpinner />
              <span>{t('modal.pix.generatingCode')}</span>
            </LoadingSpinner>
          ) : paymentStatus === 'paid' ? (
            <StatusOverlay>
              <FaCheckCircle style={{ color: '#4caf50' }} />
              <h3>{t('modal.pix.confirmed')}</h3>
              <p>{t('modal.pix.confirmedMsg')}</p>
            </StatusOverlay>
          ) : paymentStatus === 'failed' || paymentStatus === 'cancelled' ? (
            <StatusOverlay>
              <FaTimesCircle style={{ color: '#f44336' }} />
              <h3>{paymentStatus === 'failed' ? t('modal.pix.failed') : t('modal.pix.cancelled')}</h3>
              <p>{t('modal.pix.failedMsg')}</p>
            </StatusOverlay>
          ) : paymentStatus === 'expired' ? (
            <StatusOverlay>
              <FaTimesCircle style={{ color: '#ff9800' }} />
              <h3>{t('modal.pix.expired')}</h3>
              <p>{t('modal.pix.expiredMsg')}</p>
              <button
                onClick={handleRegeneratePixCode}
                style={{
                  marginTop: '16px',
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #00D68F, #00A86B)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 168, 107, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <FaPix /> {t('modal.pix.generateNewQr')}
              </button>
            </StatusOverlay>
          ) : (
            <>
              <PixIcon>
                <FaPix />
              </PixIcon>

              <Title>{t('modal.pix.title')}</Title>
              <Subtitle>{t('modal.pix.subtitle')}</Subtitle>

              <PaymentAmount>
                <span>{t('modal.pix.amount')}</span>
                <strong>{formatPriceWithSymbol(amount)}</strong>
              </PaymentAmount>

              <QRCodeContainer>
                {qrCodeUrl ? (
                  <QRCodeImage src={qrCodeUrl} alt="QR Code PIX" />
                ) : qrCodeBase64 ? (
                  <QRCodeImage src={`data:image/png;base64,${qrCodeBase64}`} alt="QR Code PIX" />
                ) : (
                  <QRCodePlaceholder>
                    <FaPix />
                    <span>{t('modal.pix.loadingQr')}</span>
                  </QRCodePlaceholder>
                )}
              </QRCodeContainer>

              <PixCodeBox>
                <PixCodeInput
                  type="text"
                  value={qrCode || ''}
                  readOnly
                  placeholder={t('modal.pix.awaitingCode')}
                />
                <CopyButton onClick={copyPixCode} $copied={copied} disabled={!qrCode}>
                  {copied ? (<><FaCheck /> {t('modal.pix.copied')}</>) : (<><FaCopy /> {t('modal.pix.copyCode')}</>)}
                </CopyButton>
              </PixCodeBox>

              <ExpirationInfo $warning={timeRemaining && (timeRemaining.startsWith('0') || timeRemaining === 'Expirado')}>
                <span>{t('modal.pix.timeRemaining')}</span>
                <strong>{timeRemaining || t('modal.pix.loadingTime')}</strong>
              </ExpirationInfo>

              <Instructions>
                <h3>{t('modal.pix.howToPay')}</h3>
                <ol>
                  <li>{t('modal.pix.step1')}</li>
                  <li>{t('modal.pix.step2')}</li>
                  <li>{t('modal.pix.step3')}</li>
                  <li>{t('modal.pix.step4')}</li>
                </ol>
              </Instructions>
            </>
          )}
        </ModalContent>
      </Modal>
    </Overlay>
  );
};

export default ModalPaymentPix;
